import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { Utils } from 'src/app/helpers/utils';
import { AuthService } from '../../../helpers/auth.service';
import { SystemDesignService } from '../../system-design/system-design.service';
import { ConfigureModalComponent } from './configure-modal/configure-modal.component';
import { SettingsService } from '../../settings/settings.service';
import { NavigationControllerService } from '@design/services/navigation-controller.service';
import { Router } from '@angular/router';
import { RouteTrackerService } from '@design/services/route-tracker.service';
import { ImageCapturer } from '@design/containers/draw-area/threejs/services/image-capturing.service';
import { FeatureFlagService } from '@api/services/feature-flags.service';
import { filter, take, takeUntil } from 'rxjs/operators';
import { SaveStatus } from '@design/models/save-status.model';
import { fromPanelArrayActions } from '@design/store/panel-array';
import { Store } from '@ngrx/store';
import { DesignState } from '@design/store/design.reducer';

@Component({ standalone: false,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})

export class HeaderComponent implements OnInit, OnDestroy {
  userInfo: any;
  menuItems: Array<{ id: number; name: string; label: string; href: string; isExternal?: boolean }> = [];
  logo = '';
  logoWidth = 70;
  logoHeight = 50;
  timeoutId: NodeJS.Timeout | undefined;
  private bannerRefreshInterval: any;
  savingInProgress: boolean = false;
  subscriptions: Subscription = new Subscription();
  url: string = '';
  isLogout: boolean = false;
  banner: { type: string; daysLeft?: number; expiresAt?: string; mailtoHref?: string } | null = null;
  upgradeRequestSent = false;
  upgradeRequestLoading = false;
  renewalRequestSent = false;
  renewalRequestLoading = false;
  private destroy$ = new Subject<void>();
  
  constructor(
    private router: Router,
    private http: HttpClient,
    public systemDesignService: SystemDesignService,
    private dialog: MatDialog,
    public authService: AuthService,
    private utils: Utils,
    private settingsService: SettingsService,
    private navigationControllerService: NavigationControllerService,
    private routeTrackerService: RouteTrackerService,
    private imageCaptureService: ImageCapturer,
    private featureFlagService: FeatureFlagService,
    private store: Store<DesignState>,

  ) {
    this.utils.countryCurr = localStorage.getItem('countryCurr') || '$';
  }

  initTrialBanner(): void {
    const status = this.authService.getItem('subscription_status', 'localStorage');
    const trialExpiry = this.authService.getItem('trial_expires_at', 'localStorage');
    const subscriptionExpiry = this.authService.getItem('subscription_expires_at', 'localStorage');

    const daysUntil = (ts: string): number =>
      Math.ceil((new Date(parseInt(ts, 10)).getTime() - Date.now()) / 86400000);

    if (status === 'trial' && trialExpiry && trialExpiry !== 'undefined') {
      const d = daysUntil(trialExpiry);
      if (d < 0) {
        this.authService.setItem('subscription_status', 'trial_expired', 'localStorage');
        this.router.navigate(['/trial-expired']);
        return;
      }
      if (d <= 7) {
        this.banner = { type: 'trial', daysLeft: d };
      }
    }

    if (status === 'subscription_expired') {
      // Status was set by old code — show overdue banner, full access retained
      this.banner = { type: 'renewal_grace' };
    } else if (status === 'active' && subscriptionExpiry && subscriptionExpiry !== 'undefined') {
      const d = daysUntil(subscriptionExpiry);
      if (d < 0) {
        // Subscription has expired — show overdue banner, full access retained
        this.banner = { type: 'renewal_grace' };
      } else if (d <= 7) {
        // Within 7 days before expiry — show countdown renewal banner
        this.banner = { type: 'renewal', daysLeft: d };
      }
    }
  }

  requestRenewal(): void {
    if (this.renewalRequestSent || this.renewalRequestLoading) return;
    const sessionId = this.authService.getItem('sessionId', 'localStorage');
    if (!sessionId) return;

    this.renewalRequestLoading = true;
    const url = `${Utils.getBaseurl()}public/signup/request-renewal?sessionId=${encodeURIComponent(sessionId)}`;
    this.http.post(url, null).subscribe({
      next: () => {
        this.renewalRequestLoading = false;
        this.renewalRequestSent = true;
      },
      error: () => {
        this.renewalRequestLoading = false;
      }
    });
  }

  requestUpgrade(): void {
    if (this.upgradeRequestSent || this.upgradeRequestLoading) return;
    const sessionId = this.authService.getItem('sessionId', 'localStorage');
    if (!sessionId) return;

    this.upgradeRequestLoading = true;
    const url = `${Utils.getBaseurl()}public/signup/request-upgrade?sessionId=${encodeURIComponent(sessionId)}`;
    this.http.post(url, null).subscribe({
      next: () => {
        this.upgradeRequestLoading = false;
        this.upgradeRequestSent = true;
      },
      error: () => {
        this.upgradeRequestLoading = false;
      }
    });
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.timeoutId) clearInterval(this.timeoutId);
    if (this.bannerRefreshInterval) clearInterval(this.bannerRefreshInterval);
  }

  getServicesOffered() {
    this.subscriptions.add(this.settingsService.getServicesOffered(['services_offered']).subscribe((res: Array<any> | any) => {
      this.settingsService.setServiceOffered(res);
      this.initMenus();
    }, (error: any) => {
      console.log(error);
    }));
  }
  
  ngOnInit(): void {
    //get flag from launch darkly to show/hide fleetview
    this.featureFlagService.getFlagsFromServer();
    this.getServicesOffered();
    this.getNavigationInfo();
    this.initTrialBanner();
    // Refresh the trial countdown every hour so days-remaining stays current
    // without requiring the user to log out and back in.
    this.bannerRefreshInterval = setInterval(() => this.initTrialBanner(), 60 * 60 * 1000);
  }

  getNavigationInfo() {
    this.subscriptions.add(this.navigationControllerService.canNavigate().subscribe((canNavigate: boolean) => {
      this.savingInProgress = !canNavigate;
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        if(!this.savingInProgress){
          if(this.url){
            this.navigateMe(this.url);
          }else if(this.isLogout){
            this.logout();
          }
        }
      }, 2000) as any;
    }));
  }

  navigateMe(url: string) {
    if (this.routeTrackerService.getCurrentUrl() == '/design') {
      this.url = url;
      if (!this.savingInProgress) {
        this.url = '';
        this.authService.enableDesignLoader.next({ val: true, action: 'stop' });
        this.router.navigateByUrl(url);
      } else {
        this.authService.enableDesignLoader.next({ val: true, action: 'start', fromEvent: 'saveDesign' })
      }
    } else {
      this.router.navigateByUrl(url);
    }
  }
  
  initMenus() {
    // STEP 1: Initialize with default menu items
    this.menuItems = [
        { id: 0, name: 'PROJECTS', label: 'PROJECTS', href: 'projects' },
        { id: 1, name: 'SETTINGS', label: 'SETTINGS', href: 'settings' }
    ];
    
    // STEP 2: Wait for feature flags to load to determine monitor-fleet
    this.featureFlagService.isFeatureFlagsReady$
      .pipe(
        takeUntil(this.destroy$),
        filter(isFlagReady => isFlagReady),
        take(1)
      )
      .subscribe(() => {
        const showMonitorFleet = this.featureFlagService.getFlag('monitor-fleet');
        //If true, add monitor fleet option
        if (showMonitorFleet) {
            const monitorFleetItem = {
              id: 2,
              name: 'MONITOR FLEET',
              label: 'MONITOR FLEET',
              href: 'fleet'
            };
            // Insert at index 1
            this.menuItems.splice(1, 0, monitorFleetItem);
          }
        const showFleetDashboard = this.featureFlagService.getFlag('fleet-dashboard');
        //If true, add monitor fleet option
        if (showFleetDashboard) {

            const fleetDashboardItem = {
              id: 5,
              name: 'FLEET DASHBOARD',
              label: 'FLEET DASHBOARD',
              href: 'grafana-dashboard'
            };
            // Insert after MONITOR FLEET
            const fleetIdx = this.menuItems.findIndex(item => item.name === 'MONITOR FLEET');
            this.menuItems.splice(fleetIdx + 1, 0, fleetDashboardItem);
          }

        // Add Tickets link if ticketing flag returns a URL (gated by enact-ticketing segment)
        const ticketingUrl = this.featureFlagService.getFlag('ticketing');
        if (ticketingUrl) {
            const ticketsItem = {
              id: 3,
              name: 'TICKETS',
              label: 'TICKETS',
              href: ticketingUrl,
              isExternal: true
            };
            // Insert after MONITOR FLEET (or after PROJECTS if fleet is hidden)
            const insertIndex = this.menuItems.findIndex(item => item.name === 'SETTINGS');
            this.menuItems.splice(insertIndex, 0, ticketsItem);
          }
      });

    this.userInfo = this.authService.getItem('username');

    // Close the dropdown if the user clicks outside of it
    window.onclick = function (e: any) {
      if (!e.target.matches('.dropbtn')) {
        var myDropdown: any = document.getElementById('myDropdown');
        if (myDropdown) {
          if (myDropdown.classList.contains('show')) {
            myDropdown.classList.remove('show');
          }
        }
      }
    };

    this.subscriptions.add(
      this.systemDesignService.getTenantData().subscribe(
        (response: { imageId: any, mapProvider: string }) => {
          if(response?.mapProvider)
            this.authService.defMapProvider = response?.mapProvider;
          if(response.imageId) { // Condition to avoid 'undefined' campaign-images call for new quote
          this.subscriptions.add(
            this.systemDesignService.getLogo(response.imageId).subscribe(
              (data: string) => {
                this.loadLogo(data);
              },
              (error: any) => {
                this.logo = '../../../../assets/images/login/enact-logo-sm.jpg';
              }
            )
          );
          }
        },
        (error: any) => {
          this.logo = '../../../../assets/images/login/enact-logo-sm.jpg';
        }
      )
    );
  }
  loadLogo(data: any) {
    const that = this;
    const imgurl = 'data:image/jpg;base64,' + data;
    that.logo = 'data:image/jpg;base64,' + data;
    let img = new Image();
    img.src = imgurl;
    img.onload = function (event) {
      const loadedImage: any = event.currentTarget;
      let width = loadedImage.width;
      let height = loadedImage.height;
      let aspectRatio = width / height;
      if (height != 50) {
        that.logoHeight = 50;
        that.logoWidth = that.logoHeight * aspectRatio;
      }
    };
  }
  /* When the user clicks on the button, 
toggle between hiding and showing the dropdown content */
  myFunction() {
    var dropdown: any = document.getElementById('myDropdown');
    dropdown.classList.toggle('show');
  }

  callImageCapture() {
    if (this.routeTrackerService.getCurrentUrl() !== '/design') return;

    if (this.authService.triggerHourlyOutOfDesign === SaveStatus.OUTDATED) {
      this.store.dispatch(fromPanelArrayActions.pvWattsRequestParamChange());
    }

    this.imageCaptureService.callImageCapture();
  }


  logout(): void {
    if (this.routeTrackerService.getCurrentUrl() == '/design') {
      this.isLogout = true;
      if (!this.savingInProgress) {
        this.isLogout = false;
        this.authService.enableDesignLoader.next({ val: true, action: 'stop' });
        this.authService.logout();
      } else {
        this.authService.enableDesignLoader.next({ val: true, action: 'start', fromEvent: 'saveDesign' })
      }
    } else {
      this.authService.logout();
    }
  }
  showModal() {
    const dialogRef = this.dialog.open(ConfigureModalComponent, {
      width: '900px',
      height: '560px',
      panelClass: 'proposal-modal',
    });
  }
}
